import React, { useState } from 'react';
import { CheckIcon, Share2Icon } from 'lucide-react';
import { ShareCardFrame } from '../engagement/ShareCardFrame';
import { ShareSheet } from '../engagement/ShareSheet';
import { Button } from '../ui/Button';
import { Switch } from '../ui/Switch';
import { communities } from '../../data/communities';
import { PROTOTYPE_SHARE_SIMULATION } from '../../data/recapConfig';
import { useRecapConfig } from '../../hooks/recapConfigStore';
import { useEngagement } from '../../contexts/EngagementContext';
import { useLocale } from '../../contexts/LocaleContext';
import type { BeeArchetypeId, Recap } from '../../types/engagement';
import { hiveSnapshot, percentileEligible, strongestHabits } from '../../utils/recap';
type OptionId = 'streak' | 'challenges' | 'activity' | 'community';

/**
 * Final recap share card. Defaults: period, Bee type, Hive evolution and two positive
 * highlights. Extra items are opt-in. Health data is never available here.
 */
export function RecapShareCard({
  recap,
  archetype,
  communityId
}: {
  recap: Recap;
  archetype: BeeArchetypeId;
  communityId: string;
}) {
  const {
    t,
    tl
  } = useLocale();
  const engagement = useEngagement();
  const [options, setOptions] = useState<Record<OptionId, boolean>>({
    streak: false,
    challenges: false,
    activity: false,
    community: false
  });
  const [sharing, setSharing] = useState(false);
  const [rewardNote, setRewardNote] = useState<'granted' | 'already' | null>(null);
  const config = useRecapConfig();
  const start = hiveSnapshot(recap.hiveStartBee);
  const end = hiveSnapshot(recap.hiveEndBee);
  const top = strongestHabits(recap);
  const context = recap.communityContext.find(item => item.communityId === communityId);
  const communityOk = percentileEligible(context);
  const community = communities.find(item => item.id === communityId);
  const reward = config.shareRewardBee;
  const alreadyRewarded = engagement.rewardedRecaps.includes(recap.id);
  const toggle = (id: OptionId) => (on: boolean) => setOptions(prev => ({
    ...prev,
    [id]: on
  }));
  const rows: {
    label: string;
    value: string;
  }[] = [{
    label: t('eng.recap.habit'),
    value: top.map(id => t(`habit.${id}.title`)).join(' · ')
  }, {
    label: t('eng.recap.checkDays'),
    value: String(recap.checkDays)
  }];
  if (options.streak) rows.push({
    label: t('eng.recap.streak'),
    value: t('eng.recap.streakValue', {
      n: recap.longestStreak
    })
  });
  if (options.challenges) rows.push({
    label: t('eng.recap.share.opt.challenges'),
    value: String(recap.challengesDone)
  });
  if (options.activity) rows.push({
    label: t('eng.recap.activity'),
    value: tl(recap.mainActivity)
  });

  // Picking Instagram / WhatsApp / another destination is only an intent — BEELYV cannot
  // verify an external post today, so it never grants Bee. The reward needs a confirmed
  // share event (Marco 3); the prototype demonstrates it with an explicit demo action.
  const simulateConfirmedShare = () => {
    setRewardNote(engagement.claimRecapShareReward(recap.id) ? 'granted' : 'already');
  };
  const optionList: {
    id: OptionId;
    label: string;
    disabled?: boolean;
  }[] = [{
    id: 'streak',
    label: t('eng.recap.share.opt.streak')
  }, {
    id: 'challenges',
    label: t('eng.recap.share.opt.challenges')
  }, {
    id: 'activity',
    label: t('eng.recap.share.opt.activity')
  }, {
    id: 'community',
    label: t('eng.recap.share.opt.community'),
    disabled: !communityOk
  }];
  return <section aria-label={t('eng.recap.share.overline')} data-id="element-1378" data-mp="m32buloohojl">
      <ShareCardFrame label={t('eng.recap.share.cardLabel')} data-id="element-1379" data-mp="r36twg58irbx">
        <p className="bee-overline text-[8.5px] font-bold text-ondark" data-id="element-1380" data-mp="r1lyypf0d6dj">{tl(recap.periodLabel)}</p>
        <p className="mt-1 text-[22px] font-bold leading-tight text-gold" data-id="element-1381" data-mp="vuf1r7qx24mz">{tl(config.archetypes[archetype].name)}</p>
        <p className="mt-3 text-[10.5px] text-ondark" data-id="element-1382" data-mp="10v2qtnvl4bek">{t('eng.recap.share.hive')}</p>
        <p className="bee-num text-[15px] font-bold text-sand" data-id="element-1383" data-mp="14w64q4u2nxd5">
          {t('eng.recap.hive.delta', {
          from: start.cells,
          to: end.cells
        })}
        </p>
        {start.stage.id !== end.stage.id && <p className="text-[11px] text-sand" data-id="element-1384" data-mp="w54ml5h1uwvs">
            {tl(start.stage.name)} → {tl(end.stage.name)}
          </p>}
        <ul className="mt-3 space-y-1 text-[11.5px]" data-id="element-1385" data-mp="15wr6dq7ge4a5">
          {rows.map(row => <li key={row.label} className="flex justify-between gap-2" data-id="element-1386" data-mp="15yvao999l5ay">
              <span className="text-ondark" data-id="element-1387" data-mp="10y7k1ooy6qh9">{row.label}</span>
              <span className="bee-num text-right font-bold" data-id="element-1388" data-mp="d7jxbxadhwan">{row.value}</span>
            </li>)}
        </ul>
        {options.community && communityOk && context && <p className="mt-2 text-[10.5px] leading-snug text-sand" data-id="element-1389" data-mp="n69h5vltxqtj">
            {t(recap.period === 'month' ? 'eng.recap.pct.month' : 'eng.recap.pct.year', {
          n: context.topPercent,
          community: community ? tl(community.name) : communityId
        })}
          </p>}
      </ShareCardFrame>

      <div className="mt-3 rounded-card border border-sand-line bg-white/70 px-2" data-id="element-1390" data-mp="1tn4mqjgoeeow">
        <p className="pt-2 text-[12px] font-bold text-deep" data-id="element-1391" data-mp="3bjghv50fqp4k">{t('eng.recap.share.options')}</p>
        <ul className="divide-y divide-sand-line" data-id="element-1392" data-mp="3git5hrz4s087">
          {optionList.map(option => <li key={option.id} className="flex items-center justify-between gap-2 py-2" data-id="element-1393" data-mp="32jjncmrd6w1l">
              <span className="min-w-0" data-id="element-1394" data-mp="37id36lu4iz4x">
                <span className={`block text-[13px] ${option.disabled ? 'text-muted' : 'text-deep'}`} data-id="element-1395" data-mp="37jecakrkxbk0">
                  {option.label}
                </span>
                {option.disabled && <span className="block text-[11px] text-muted" data-id="element-1396" data-mp="333j18cjkn8y">{t('eng.recap.share.opt.communityOff')}</span>}
              </span>
              {option.disabled ? <span className="text-[11px] text-muted" data-id="element-1397" data-mp="3qgflkhs61xtr">—</span> : <Switch checked={options[option.id]} onChange={toggle(option.id)} label={option.label} data-id="element-1398" data-mp="i1p5b1h1s15a" />}
            </li>)}
        </ul>
        <p className="pb-2 text-[11px] leading-snug text-muted" data-id="element-1399" data-mp="mtgq0vdivcni">{t('eng.recap.share.never')}</p>
      </div>

      <div className="mt-3 rounded-card border border-gold/50 bg-gold/10 p-2" data-id="element-1400" data-mp="wm1m2nxpvzxk">
        <p className="text-[12.5px] font-bold text-deep" data-id="element-1401" data-mp="rpks3d3u6rck">{t('eng.recap.reward.title')}</p>
        <p role="status" className="mt-[2px] text-[12px] leading-snug text-deep" data-id="element-1402" data-mp="monbv62e07dp">
          {rewardNote === 'granted' ? t('eng.recap.reward.granted', {
          n: reward
        }) : rewardNote === 'already' || alreadyRewarded ? t('eng.recap.reward.already') : t('eng.recap.reward.eligible', {
          n: reward
        })}
        </p>
        <p className="mt-1 text-[11px] leading-snug text-muted" data-id="element-1403" data-mp="3z74bysabens">{t('eng.recap.reward.verification')}</p>
        <p className="mt-1 text-[11px] text-muted" data-id="element-1404" data-mp="3v69y8xzibz1p">
          {t('eng.recap.reward.optional')} {t('eng.recap.reward.saveNote')}
        </p>
      </div>

      {PROTOTYPE_SHARE_SIMULATION && !alreadyRewarded && <div className="mt-2 rounded-card border border-dashed border-deep/30 p-2" data-id="element-1405" data-mp="3m4tngp4s5uwy">
          <p className="bee-overline text-[8.5px] font-bold text-muted" data-id="element-1406" data-mp="21nkg5384kve1">{t('eng.recap.reward.demoLabel')}</p>
          <Button variant="outline" fullWidth className="mt-1" onClick={simulateConfirmedShare} data-id="element-1407" data-mp="25nuxfc1db887">
            {t('eng.recap.reward.simulate')}
          </Button>
          <p className="mt-1 text-[11px] leading-snug text-muted" data-id="element-1408" data-mp="2alwfpuqyz3im">{t('eng.recap.reward.demoNote')}</p>
        </div>}

      <Button variant="primary" size="lg" fullWidth className="mt-3" onClick={() => setSharing(true)} data-id="element-1409" data-mp="1hzbp4wibivg4">
        {alreadyRewarded ? <CheckIcon size={16} aria-hidden="true" data-id="element-1410" data-mp="1mz6f8xu9rh04" /> : <Share2Icon size={16} aria-hidden="true" data-id="element-1411" data-mp="1mw25zm1y5v2l" />}
        {t('eng.recap.share.cta')}
      </Button>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} data-id="element-1412" data-mp="1rv51aes4kmxd" />
    </section>;
}